import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEye, FaEyeSlash, FaPlus, FaSearch, FaEdit, FaTrash, FaGripHorizontal, FaCalendarAlt, FaCloudUploadAlt, FaCloudDownloadAlt } from 'react-icons/fa';
import { 
  TextField, 
  IconButton,
  Box,
  InputAdornment,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  OutlinedInput
} from '@mui/material';
import { Container, Header, HeaderLeft, Title, Content, BackButton, HeaderButton, ControlsContainer } from '../../common/styles';
import { MainContainer, MatrixGrid, MatrixCard, MatrixCardContent, MatrixTitle, MatrixDetailsGrid, ActionButtons } from './styles';
import ConfirmationModal from '../../common/ConfirmationModal';
import Modal from '../../common/Modal';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import { useAlert } from '../../../contexts/AlertContext';
import { skillsApi } from '../../../services/api';
import { COLORS } from '../../../constants/radar';
import { REVIEW_TYPES, getReviewTypeColor, getReviewTypeText, getReviewTypeTextColor } from '../../../constants/reviewTypes';
import { getClusterChipStyle } from '../../../utils/clusterUtils';
import ClusterSelector from '../../common/ClusterSelector';
import { ReviewTypeBadge } from '../../common/ReviewTypeBadge';
import { toLocalDate } from '../../../utils/dateOnly';

const getSteps = () => ['Session Details', 'Clusters'];

const SkillsMatrixSessions = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [showControls, setShowControls] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [modalState, setModalState] = useState({
    isOpen: false,
    mode: 'add',
    session: null,
    formData: { 
      name: '', 
      reviewType: '', 
      description: '',
      selectedClusters: []
    }
  });
  const [deleteModal, setDeleteModal] = useState({ open: false, session: null });
  const [publishModal, setPublishModal] = useState({ open: false, session: null });
  const [unpublishModal, setUnpublishModal] = useState({ open: false, session: null });
  const [currentStep, setCurrentStep] = useState(1);

  const fetchSessions = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await skillsApi.getAllSkillsMatrixSessions();
      setSessions(data);
    } catch (err) {
      showAlert('Failed to load skills matrix sessions.', 'error');
      console.error('Error fetching skills matrix sessions');
    } finally {
      setIsLoading(false);
    }
  }, [showAlert]);



  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const filteredSessions = sessions.filter(session =>
    session.name.toLowerCase().includes((searchTerm || '').toLowerCase()) ||
    session.reviewType.toLowerCase().includes((searchTerm || '').toLowerCase()) ||
    (session.description && session.description.toLowerCase().includes((searchTerm || '').toLowerCase()))
  );

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const handleModalOpen = (mode, session = null) => {
    let selectedClusters = [];
    
    if (session) {
      // Handle different possible data structures for clusters
      if (session.clusters && Array.isArray(session.clusters)) {
        selectedClusters = session.clusters.map(item => {
            return item.clusterId;
        });
      }
    }
    
    setModalState({
      isOpen: true,
      mode,
      session,
      formData: session ? {
        name: session.name,
        reviewType: session.reviewType,
        description: session.description || '',
        selectedClusters: selectedClusters
      } : { 
        name: '', 
        reviewType: '', 
        description: '',
        selectedClusters: []
      }
    });
    setCurrentStep(1);
  };

  const handleModalClose = () => {
    setModalState({
      isOpen: false,
      mode: 'add',
      session: null,
      formData: { 
        name: '', 
        reviewType: '', 
        description: '',
        selectedClusters: []
      }
    });
    setCurrentStep(1);
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleNext = () => {
    if (currentStep === 1) {
      if (!modalState.formData.name.trim()) {
        showAlert('Please enter a session name', 'error');
        return;
      }
      if (!modalState.formData.reviewType) {
        showAlert('Please select a review type', 'error');
        return;
      }
    }
    if (currentStep === 2) {
      if (modalState.formData.selectedClusters.length === 0) {
        showAlert('Please select at least one cluster', 'error');
        return;
      }
    }
    if (currentStep < 2) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleSubmit = async () => {
    if (currentStep < 2) {
      handleNext();
      return;
    }

    const submitData = {
      name: modalState.formData.name,
      reviewType: modalState.formData.reviewType,
      description: modalState.formData.description,
      clusterIds: modalState.formData.selectedClusters
    };

    try {
      setIsLoading(true);
      if (modalState.mode === 'add') {
        await skillsApi.createSkillsMatrixSession(submitData);
        showAlert('Skills matrix session created successfully', 'success');
      } else {
        await skillsApi.updateSkillsMatrixSession(modalState.session.id, submitData);
        showAlert('Skills matrix session updated successfully', 'success');
      }
      await fetchSessions();
      handleModalClose();
    } catch (err) {
      showAlert(`Failed to ${modalState.mode} skills matrix session. Please try again.`, 'error');
      console.error(`Error ${modalState.mode}ing skills matrix session`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSession = (session) => {
    setDeleteModal({ open: true, session });
  };

  const confirmDelete = async () => {
    try {
      setIsLoading(true);
      await skillsApi.deleteSkillsMatrixSession(deleteModal.session.id);
      showAlert('Skills matrix session deleted successfully', 'success');
      await fetchSessions();
      setDeleteModal({ open: false, session: null });
    } catch (err) {
      showAlert('Failed to delete skills matrix session. Please try again.', 'error');
      console.error('Error deleting skills matrix session');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePublishClick = (session) => {
    setPublishModal({ open: true, session });
  };

  const confirmPublish = async () => {
    try {
      setIsLoading(true);
      await skillsApi.publishSkillsMatrixSession(publishModal.session.id);
      showAlert('Session published successfully', 'success');
      await fetchSessions();
      setPublishModal({ open: false, session: null });
    } catch (err) {
      showAlert('Failed to publish session. Please try again.', 'error');
      console.error('Error publishing session');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnpublishClick = (session) => {
    setUnpublishModal({ open: true, session });
  };

  const confirmUnpublish = async () => {
    try {
      setIsLoading(true);
      await skillsApi.unpublishSkillsMatrixSession(unpublishModal.session.id);
      showAlert('Session unpublished successfully', 'success');
      await fetchSessions();
      setUnpublishModal({ open: false, session: null });
    } catch (err) {
      showAlert('Failed to unpublish session. Please try again.', 'error');
      console.error('Error unpublishing session');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClusterSelection = (clusterIds) => {
    setModalState(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        selectedClusters: clusterIds
      }
    }));
  };



  const getStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <Box sx={{ p: 2 }}>
            <Box sx={{ mb: 3 }}>
              <TextField
                label="Session Name"
                name="name"
                required
                fullWidth
                variant="outlined"
                placeholder="Enter session name"
                value={modalState.formData.name}
                onChange={(e) => setModalState(prev => ({
                  ...prev,
                  formData: { ...prev.formData, name: e.target.value }
                }))}
                autoComplete="off"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '56px'
                  }
                }}
              />
            </Box>
            <Box sx={{ mb: 3 }}>
              <TextField
                label="Description"
                name="description"
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                placeholder="Enter session description (optional)"
                value={modalState.formData.description}
                onChange={(e) => setModalState(prev => ({
                  ...prev,
                  formData: { ...prev.formData, description: e.target.value }
                }))}
                autoComplete="off"
              />
            </Box>
            <FormControl fullWidth required>
              <InputLabel>Review Type</InputLabel>
              <Select
                value={modalState.formData.reviewType}
                onChange={(e) => setModalState(prev => ({
                  ...prev,
                  formData: { ...prev.formData, reviewType: e.target.value }
                }))}
                label="Review Type"
              >
                {Object.values(REVIEW_TYPES).map((type) => (
                  <MenuItem key={type} value={type}>
                    {getReviewTypeText(type)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );
      case 2:
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Select Clusters
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Choose the clusters where this session will be available. Only leaf clusters can be selected.
            </Typography>
            <ClusterSelector
              multiple
              leafOnly
              value={modalState.formData.selectedClusters}
              onChange={handleClusterSelection}
              label="Attached to"
              showStructureType // ADR-119
              helperText="Only leaf clusters can be selected"
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/skills-matrix')}>
            <FaArrowLeft /> Back to Skills Matrix
          </BackButton>
          <Title>Skills Matrix Sessions</Title>
        </HeaderLeft>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <HeaderButton onClick={() => setShowControls(!showControls)}>
            {showControls ? <FaEyeSlash /> : <FaEye />}
            {showControls ? 'Hide Panels' : 'Show Panels'}
          </HeaderButton>
        </Box>
      </Header>

      <Content>
        <ControlsContainer $show={showControls} style={{ marginBottom: '20px' }}>
          <Box sx={{ 
            display: 'flex', 
            gap: '16px', 
            flexWrap: 'wrap',
            width: '100%',
            alignItems: 'flex-start',
            justifyContent: 'space-between'
          }}>
            <TextField
              placeholder="Search Sessions"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoComplete="off"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FaSearch style={{ color: '#6B7280' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                maxWidth: '500px',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  height: '40px',
                  fontSize: '14px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#8B5CF6',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#8B5CF6',
                  }
                },
                '& .MuiInputBase-input': {
                  padding: '8px 14px',
                }
              }}
            />

            <HeaderButton 
              onClick={() => handleModalOpen('add')}
              sx={{
                minWidth: 'auto',
                height: '40px',
                padding: '0 16px',
                backgroundColor: '#8B5CF6',
                color: 'white',
                alignSelf: 'flex-start',
                '&:hover': {
                  backgroundColor: '#7C3AED',
                }
              }}
            >
              <FaPlus style={{ marginRight: '8px' }} /> Add Session
            </HeaderButton>
          </Box>
        </ControlsContainer>

        <MainContainer show={showControls}>
          <div style={{ 
            color: '#64748B', 
            fontSize: '14px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <span style={{ fontWeight: 600 }}>
              {filteredSessions.length}
            </span>
            {filteredSessions.length === 1 ? 'session' : 'sessions'} found
          </div>
          {filteredSessions.length === 0 ? (
            <EmptyState
              title="No skills matrix sessions found"
              description={searchTerm ? 
                "No sessions match your search criteria. Try adjusting your search term." : 
                "No skills matrix sessions have been created yet. Click the '+ Add Session' button to create your first session."}
            />
          ) : (
            <MatrixGrid>
              {filteredSessions.map((session) => (
                <MatrixCard key={session.id}>
                  <MatrixCardContent>
                    <MatrixTitle>
                      {session.name}
                      <ReviewTypeBadge 
                        reviewType={session.reviewType}
                        getReviewTypeColor={getReviewTypeColor}
                        getReviewTypeTextColor={getReviewTypeTextColor}
                        getReviewTypeText={getReviewTypeText}
                      />
                    </MatrixTitle>
                    <MatrixDetailsGrid>
                      {session.description && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic' }}>
                            {session.description}
                          </Typography>
                        </Box>
                      )}

                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            <strong>Available in</strong>
                          </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        {session.clusters?.length > 0 ? (
                          session.clusters.map(clusterItem => {
                            // Handle the cluster structure: { cluster: { id, name }, clusterId, ... }
                            const cluster = clusterItem.cluster || clusterItem;
                            const chipStyle = getClusterChipStyle();
                            return (
                              <Chip
                                key={cluster.id || clusterItem.clusterId}
                                /* ADR-119: NAME · CODE, as ClusterSelector renders a
                                   selected cluster; full type name on hover. */
                                label={cluster.orgStructureType?.code
                                  ? `${cluster.name} · ${cluster.orgStructureType.code}`
                                  : cluster.name}
                                title={cluster.orgStructureType?.name || undefined}
                                size="small"
                                sx={chipStyle}
                              />
                            );
                          })
                        ) : (
                          <Typography variant="body2" sx={{ fontStyle: 'italic', color: COLORS.GRAY }}>
                            No clusters assigned
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Session Details</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 0.5, color: '#666' }}>
                          Version: {session.version || 'v1.0'}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 0.5, color: '#666' }}>
                          Published: {session.publishedAt ? 
                            toLocalDate(session.publishedAt)?.toLocaleDateString() : 
                            'Not published'
                          }
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          Created: {session.createdAt ? 
                            new Date(session.createdAt).toLocaleDateString() : 
                            'Unknown'
                          }
                        </Typography>
                      </Box>
                    </MatrixDetailsGrid>
                    <ActionButtons>
                      {!session.publishedAt && (
                        <IconButton
                          onClick={() => handlePublishClick(session)}
                          color="primary"
                          size="small"
                          title="Publish Session"
                        >
                          <FaCloudUploadAlt />
                        </IconButton>
                      )}
                      {session.publishedAt && (
                        <IconButton
                          onClick={() => handleUnpublishClick(session)}
                          color="primary"
                          size="small"
                          title="Unpublish Session"
                        >
                          <FaCloudDownloadAlt />
                        </IconButton>
                      )}
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => handleModalOpen('edit', session)}
                        title="Edit Session"
                      >
                        <FaEdit />
                      </IconButton>
                      <IconButton
                        onClick={() => navigate(`/skills-matrix/sessions/${session.id}`)}
                        color="primary"
                        size="small"
                        title="View Session"
                      >
                        <FaCalendarAlt />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleDeleteSession(session)}
                        color="error"
                        title="Delete Session"
                      >
                        <FaTrash />
                      </IconButton>
                    </ActionButtons>
                  </MatrixCardContent>
                </MatrixCard>
              ))}
            </MatrixGrid>
          )}
        </MainContainer>
      </Content>

      {/* Session Modal */}
      <Modal
        isOpen={modalState.isOpen}
        onClose={handleModalClose}
        title={modalState.mode === 'add' ? 'Add New Session' : 'Edit Session'}
        onSubmit={handleSubmit}
        submitText={currentStep < 2 ? 'Next' : (modalState.mode === 'add' ? 'Create Session' : 'Save Changes')}
        cancelText={currentStep === 1 ? 'Cancel' : 'Back'}
        onCancel={currentStep === 1 ? handleModalClose : handleBack}
        submitDisabled={
          currentStep === 1 ? 
            (!modalState.formData.name.trim() || !modalState.formData.reviewType) :
            modalState.formData.selectedClusters.length === 0
        }
        maxWidth={currentStep === 1 ? 'md' : 'md'}
      >
        <Box sx={{ width: '100%', mb: 4 }}>
          <Stepper activeStep={currentStep - 1}>
            {getSteps().map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
        {getStepContent()}
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, session: null })}
        onConfirm={confirmDelete}
        title="Delete Session"
        message={`Are you sure you want to delete the session "${deleteModal.session?.name}"? This action cannot be undone.`}
      />

      {/* Publish Confirmation Modal */}
      <ConfirmationModal
        isOpen={publishModal.open}
        onClose={() => setPublishModal({ open: false, session: null })}
        onConfirm={confirmPublish}
        title="Publish Session"
        message={`Are you sure you want to publish the session "${publishModal.session?.name}"? This action cannot be undone.`}
        actionButtonText="Publish"
      />

      {/* Unpublish Confirmation Modal */}
      <ConfirmationModal
        isOpen={unpublishModal.open}
        onClose={() => setUnpublishModal({ open: false, session: null })}
        onConfirm={confirmUnpublish}
        title="Unpublish Session"
        message={`Are you sure you want to unpublish the session "${unpublishModal.session?.name}"? This action cannot be undone.`}
        actionButtonText="Unpublish"
      />
    </Container>
  );
};

export default SkillsMatrixSessions; 